import { useState } from 'react';
import { getApiErrorMessage } from '@/api/errors';
import useProductListMutations from '@/api/mutations/useProductListMutations';
import useGetProductListQuery from '@/api/queries/useGetProductListQuery';
import CustomToast from '@/components/CustomToast';
import PageHeader from '@/components/PageHeader';
import { ProductVariantSelectItem } from '@/components/ProductVariantSelectBox';
import { invalidateProductVariantSelectBoxes } from '@/components/productVariantSelectBoxQueryKey';
import useSearch from '@/components/search/useSearch';
import useStagedSelection from '@/hooks/useStagedSelection';
import { Sku } from '@/models/Sku';
import ProductListDetailHeader from '@/modules/product/components/ProductListDetailHeader';
import ProductListLoadingState from '@/modules/product/components/ProductListLoadingState';
import ProductListProductTable from '@/modules/product/components/ProductListProductTable';
import ProductListSkuAddForm from '@/modules/product/components/ProductListSkuAddForm';
import ProductListUnavailableState from '@/modules/product/components/ProductListUnavailableState';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const ProductListProductList = () => {
  const queryClient = useQueryClient();
  const { page, handlePageChange } = useSearch();
  const { productListId } = useParams();
  const { productList, isLoading, error } = useGetProductListQuery(productListId);
  const { addSkusToProductList, removeSkusFromProductList } = useProductListMutations();
  const staged = useStagedSelection<Sku>();
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) {
    return <ProductListLoadingState label="Loading Product List" />;
  }

  if (!productListId || !productList || error) {
    return <ProductListUnavailableState />;
  }

  const handleSelect = (item?: ProductVariantSelectItem | null) => {
    if (item) {
      staged.addItem(item.sku);
    }
  };

  const handleAdd = async () => {
    if (staged.totalCount === 0) {
      return;
    }

    setSubmitting(true);
    try {
      await addSkusToProductList({ productListId, ids: staged.stagedIds as string[] });
      staged.clear();
      await invalidateProductVariantSelectBoxes(queryClient);
    } catch (caught) {
      const message = await getApiErrorMessage(
        caught,
        'Failed to add the selected SKUs. Please try again.',
      );

      toast.custom((t) => (
        <CustomToast id={t.id} message={message} visible={t.visible} variant="error" />
      ));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (skuId: string) => {
    await removeSkusFromProductList({ productListId, ids: [skuId] });
    await invalidateProductVariantSelectBoxes(queryClient);
  };

  return (
    <div>
      <ProductListDetailHeader productList={productList} />

      <PageHeader title="Products in this list" />

      <ProductListSkuAddForm
        onSelect={handleSelect}
        onDeselect={(item) => staged.removeItem(item.id)}
        stagedCount={staged.totalCount}
        excludedIds={staged.stagedIds}
        productListId={productListId}
        onAdd={handleAdd}
        submitting={submitting}
      />

      <ProductListProductTable
        productListId={productListId}
        stagedItems={staged.stagedItems}
        onUnstage={staged.removeItem}
        page={page}
        onChangePage={handlePageChange}
        onRemove={handleRemove}
      />
    </div>
  );
};

export default ProductListProductList;
